// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import axios, {
  isAxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios"
import { API_URL } from "@/config"
import { buildTraceparent } from "@/observability/sentry"

// Single shared axios instance for every `/front-api/*` and legacy
// (`/users/self`, `/apikeys/self`, `/auth/*`) call the SPA makes. It owns
// the session transport and the single 401 handler. The credential is an
// HttpOnly cookie the browser attaches on its own, unreachable from
// application code (ADR-DRAFT-httponly-cookie-session), so `withCredentials`
// is the whole of the client's part in it. Every `queries.*.tsx` file
// imports `apiClient` from here; do not call `axios.create(...)` again at
// the call site.
//
// `X-Ebiose-Client` carries no information — its only job is forcing a
// CORS preflight that an attacker's origin cannot pass, which is the
// primary CSRF control for the ambient cookie
// (ADR-DRAFT-csrf-posture-origin-and-fetch-metadata).

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    "X-Ebiose-Client": "1",
  },
})

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  // M7: propagate the W3C trace context to the Backend so a browser
  // error captured by Sentry links to its backend trace (ADR-0039).
  // The header is attached on the *shared* client only — the single
  // egress seam every SPA call passes through.
  const traceparent = buildTraceparent()
  if (traceparent) {
    config.headers.set("traceparent", traceparent)
  }
  return config
})

// The endpoint the SPA asks "am I signed in?". Owned here because the 401
// exemption below and the caller in `queries.users.tsx` must name the same
// path — a drift between the two silently reopens the probe loop.
export const SESSION_PROBE_PATH = "/users/self"

// A 401 on the probe is the anonymous answer, not an expiry: reacting to
// it would re-ask the very question that just got answered. The maintenance
// detector exempts the same call for its own reason (`lib/http-client`).
export const isSessionProbe = (url: string | undefined): boolean =>
  (url ?? "").split("?")[0].endsWith(SESSION_PROBE_PATH)

// The probe's two verdicts on the credential: 401 refuses the cookie,
// 404 says the account it names no longer exists. Either way the visitor
// is not signed in. Every other failure means the question never got
// asked, so the session is unknown rather than absent.
export const isSessionDenied = (error: unknown): boolean =>
  isAxiosError(error) &&
  (error.response?.status === 401 || error.response?.status === 404)

let sessionExpiredHandler: (() => void) | null = null

// The handler drops the session state and leaves the routing to the
// guard, so a rejected cookie and a cold arrival take the same path.
export const setSessionExpiredHandler = (
  handler: (() => void) | null,
): void => {
  sessionExpiredHandler = handler
}

apiClient.interceptors.response.use(undefined, (error) => {
  if (error.response?.status === 401 && !isSessionProbe(error.config?.url)) {
    sessionExpiredHandler?.()
  }
  return Promise.reject(error)
})
